import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, CalendarIcon } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LoadProfileItem, SensitivityVariable } from "@shared/schema";

// Step 1: Pump/Motor Information
export function Step1PumpInfo() {
  const { t } = useTranslation();
  const { control } = useFormContext();

  return (
    <div className="space-y-6">
      {/* Basic information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <FormField
            control={control}
            name="manufacturer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.manufacturer")} *</FormLabel>
                <FormDescription>{t("form.manufacturer_prompt")}</FormDescription>
                <FormControl>
                  <Input {...field} placeholder={t("form.model")} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="model"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.model")} *</FormLabel>
                <FormDescription>{t("form.model_prompt")}</FormDescription>
                <FormControl>
                  <Input {...field} placeholder={t("form.model")} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="age"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.age")}</FormLabel>
                <FormDescription>{t("form.age_prompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={100}
                    placeholder="0"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="space-y-4">
          <FormField
            control={control}
            name="head"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.head")}</FormLabel>
                <FormDescription>{t("form.head_prompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    placeholder="0"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="flow"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.flow")}</FormLabel>
                <FormDescription>{t("form.flow_prompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    placeholder="0"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="hp"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.hp")}</FormLabel>
                <FormDescription>{t("form.hp_prompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    placeholder="0"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Nameplate Data section */}
      <div className="pt-2">
        <h3 className="text-md font-semibold text-gray-800 mb-3">Nameplate Data</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <FormField
              control={control}
              name="nameplateHP"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nameplate HP</FormLabel>
                  <FormDescription>The rated horsepower from the motor nameplate</FormDescription>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      placeholder="0"
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="nameplateKW"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nameplate kW</FormLabel>
                  <FormDescription>The rated kilowatt rating from the motor nameplate</FormDescription>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      placeholder="0"
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-4">
            <FormField
              control={control}
              name="nameplateRPM"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nameplate RPM</FormLabel>
                  <FormDescription>The rated speed from the motor nameplate in RPM</FormDescription>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      placeholder="0"
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="nameplateFLA"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nameplate FLA</FormLabel>
                  <FormDescription>The full load amps from the motor nameplate</FormDescription>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      placeholder="0"
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>

      {/* Power Draw section */}
      <div className="pt-2">
        <h3 className="text-md font-semibold text-gray-800 mb-3">Power Draw</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={control}
            name="powerDrawAmps"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Power Draw (Amps)</FormLabel>
                <FormDescription>The measured current draw in amperes during operation</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    placeholder="0"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="powerDrawLoadPercentage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Load Percentage (%)</FormLabel>
                <FormDescription>The percentage of full load at which the pump operates</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={100}
                    placeholder="0"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
}

// Step 2: Pump Application Details
export function Step2ApplicationDetails() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const variableSpeed = watch("variableSpeed");

  return (
    <div className="space-y-6">
      {/* Basic application details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <FormField
            control={control}
            name="application"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Application *</FormLabel>
                <FormDescription>Select the primary application of this pump system</FormDescription>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select application type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="chilled-water">Chilled Water</SelectItem>
                    <SelectItem value="condenser-water">Condenser Water</SelectItem>
                    <SelectItem value="hot-water">Hot Water</SelectItem>
                    <SelectItem value="cooling-tower">Cooling Tower</SelectItem>
                    <SelectItem value="booster">Booster</SelectItem>
                    <SelectItem value="process">Process</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="fluidType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Fluid Type *</FormLabel>
                <FormDescription>Select the type of fluid being pumped</FormDescription>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select fluid type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="water">Water</SelectItem>
                    <SelectItem value="glycol-mix">Glycol Mix</SelectItem>
                    <SelectItem value="chemical">Chemical</SelectItem>
                    <SelectItem value="wastewater">Wastewater</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="space-y-4">
          <FormField
            control={control}
            name="rpm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Operating RPM</FormLabel>
                <FormDescription>The actual operating speed of the pump in RPM</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    placeholder="0"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="efficiency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>System Efficiency (%)</FormLabel>
                <FormDescription>The overall efficiency of the pump system as a percentage</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={100}
                    placeholder="0"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Variable Speed section */}
      <div className="pt-2">
        <h3 className="text-md font-semibold text-gray-800 mb-3">Variable Speed</h3>
        <FormField
          control={control}
          name="variableSpeed"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Variable Speed Drive</FormLabel>
              <FormDescription>Does the pump use a variable speed drive (VSD) or variable frequency drive (VFD)?</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="variable-yes" />
                    <Label htmlFor="variable-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="variable-no" />
                    <Label htmlFor="variable-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Not Sure" id="variable-not-sure" />
                    <Label htmlFor="variable-not-sure">Not Sure</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {variableSpeed === "Yes" && (
          <FormField
            control={control}
            name="variableSpeedDetails"
            render={({ field }) => (
              <FormItem className="mt-4">
                <FormLabel>Variable Speed Details</FormLabel>
                <FormDescription>Provide details about the variable speed drive used with this pump</FormDescription>
                <FormControl>
                  <Textarea 
                    {...field} 
                    placeholder="Enter VFD details, model, age, etc."
                    className="min-h-[80px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {/* Consider Replacement */}
      <div className="pt-2">
        <FormField
          control={control}
          name="considerReplacement"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Consider Replacement</FormLabel>
              <FormDescription>Would you like to consider replacing this pump with a more efficient model?</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="consider-yes" />
                    <Label htmlFor="consider-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="consider-no" />
                    <Label htmlFor="consider-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Not Sure" id="consider-not-sure" />
                    <Label htmlFor="consider-not-sure">Not Sure</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// Step 3: Load Profile
export function Step3LoadProfile() {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();
  const loadProfile = watch("loadProfile") as LoadProfileItem[];
  const [useDetailedProfile, setUseDetailedProfile] = useState(true);
  
  // Calculate total hours
  const totalHours = loadProfile.reduce((sum, item) => sum + (item.hours || 0), 0);
  const isOverLimit = totalHours > 8760;
  
  // Update percentages based on total hours
  const updatePercentages = () => {
    if (totalHours > 0) {
      const updatedProfile = loadProfile.map(item => ({
        ...item,
        percentage: ((item.hours || 0) / totalHours) * 100
      }));
      setValue("loadProfile", updatedProfile);
    }
  };

  return (
    <div className="space-y-6">
      <FormField
        control={control}
        name="pumpSchedule"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("form.pumpSchedule")} *</FormLabel>
            <FormDescription>{t("form.pumpSchedulePrompt")}</FormDescription>
            <Select 
              onValueChange={field.onChange} 
              defaultValue={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t("form.selectSchedule")} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="24-7">{t("form.scheduleOptions.continuous")}</SelectItem>
                <SelectItem value="business-hours">{t("form.scheduleOptions.businessHours")}</SelectItem>
                <SelectItem value="seasonal">{t("form.scheduleOptions.seasonal")}</SelectItem>
                <SelectItem value="periodic">{t("form.scheduleOptions.periodic")}</SelectItem>
                <SelectItem value="variable">{t("form.scheduleOptions.variable")}</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {/* Profile Type Selection */}
      <div className="mb-4">
        <h3 className="text-md font-semibold text-gray-800 mb-3">{t("form.loadProfileType")}</h3>
        <div className="flex space-x-4">
          <Button 
            type="button" 
            variant={useDetailedProfile ? "default" : "outline"}
            onClick={() => setUseDetailedProfile(true)}
          >
            {t("form.detailedProfile")}
          </Button>
          <Button 
            type="button" 
            variant={!useDetailedProfile ? "default" : "outline"}
            onClick={() => setUseDetailedProfile(false)}
          >
            {t("form.simplifiedProfile")}
          </Button>
        </div>
      </div>
      
      {useDetailedProfile ? (
        <div>
          <h3 className="text-base font-medium mb-2">{t("form.loadProfile")}</h3>
          <p className="text-sm text-muted-foreground mb-4">{t("form.loadProfilePrompt")}</p>
          
          {isOverLimit && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t("form.loadProfileOverLimit")}
              </AlertDescription>
            </Alert>
          )}
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("form.loadRangeHeader")}</TableHead>
                  <TableHead>{t("form.hoursHeader")}</TableHead>
                  <TableHead>{t("form.percentageHeader")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadProfile.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.loadRange}</TableCell>
                    <TableCell>
                      <FormField
                        control={control}
                        name={`loadProfile.${index}.hours`}
                        render={({ field }) => (
                          <FormItem className="m-0">
                            <FormControl>
                              <Input
                                {...field}
                                type="number"
                                min={0}
                                placeholder="0"
                                onChange={(e) => {
                                  field.onChange(e.target.value ? Number(e.target.value) : 0);
                                  setTimeout(updatePercentages, 100);
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      {((item.hours || 0) / (totalHours || 1) * 100).toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <div className="mt-2 text-right">
            <p className="text-sm font-medium">
              {t("form.totalHours")}: {totalHours} / 8760
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField
            control={control}
            name="loadProfileSimplifiedSummer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.summerOperatingHours")}</FormLabel>
                <FormDescription>{t("form.summerOperatingHoursDesc")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={100}
                    placeholder={t("form.summerPlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="loadProfileSimplifiedSpringFall"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.springFallOperatingHours")}</FormLabel>
                <FormDescription>{t("form.springFallOperatingHoursDesc")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={100}
                    placeholder={t("form.springFallPlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="loadProfileSimplifiedWinter"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.winterOperatingHours")}</FormLabel>
                <FormDescription>{t("form.winterOperatingHoursDesc")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={100}
                    placeholder={t("form.winterPlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}

// Step 4: Operation & Maintenance
export function Step4OperationMaintenance() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const operatingSchedule = watch("operatingSchedule");

  return (
    <div className="space-y-6">
      {/* Operating Schedule */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <FormField
            control={control}
            name="operatingSchedule"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Operating Schedule</FormLabel>
                <FormDescription>Select the typical operating schedule for this pump</FormDescription>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select operating schedule" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="24-7">24/7 Continuous Operation</SelectItem>
                    <SelectItem value="business-hours">Business Hours Only (M-F, 8am-5pm)</SelectItem>
                    <SelectItem value="custom">Custom Schedule</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {operatingSchedule === "custom" && (
            <FormField
              control={control}
              name="operatingScheduleDetails"
              render={({ field }) => (
                <FormItem className="mt-4">
                  <FormLabel>Schedule Details</FormLabel>
                  <FormDescription>Please provide specific details about your custom operating schedule</FormDescription>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      placeholder="Describe your operating schedule (e.g., weekdays 6am-10pm, weekends 8am-6pm)"
                      className="min-h-[80px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        
        <div className="space-y-4">
          <FormField
            control={control}
            name="flexibleScheduling"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Flexible Scheduling</FormLabel>
                <FormDescription>Can this pump's operation be shifted to different hours of the day?</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="flexible-yes" />
                      <Label htmlFor="flexible-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="flexible-no" />
                      <Label htmlFor="flexible-no">No</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Maybe" id="flexible-maybe" />
                      <Label htmlFor="flexible-maybe">Maybe</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {watch("flexibleScheduling") === "Yes" && (
            <FormField
              control={control}
              name="flexibleSchedulingConstraints"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scheduling Constraints</FormLabel>
                  <FormDescription>What constraints would limit your ability to shift operating hours?</FormDescription>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      placeholder="Describe any process constraints, labor requirements, etc."
                      className="min-h-[80px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </div>
      
      {/* Maintenance Cost */}
      <div className="pt-2">
        <FormField
          control={control}
          name="maintenanceCost"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Annual Maintenance Cost</FormLabel>
              <FormDescription>Estimated annual maintenance cost in USD</FormDescription>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  min={0}
                  max={100000}
                  placeholder="e.g., 2000"
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// Step 5: Electrical Setup
export function Step5ElectricalSetup() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const electricalSetup = watch("electricalSetup");

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Main Electrical Setup */}
        <div className="space-y-4">
          <FormField
            control={control}
            name="electricalSetup"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Electrical Setup *</FormLabel>
                <FormDescription>Select the electrical configuration for this pump</FormDescription>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select electrical setup" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="208-3-60">208V, 3-phase, 60Hz</SelectItem>
                    <SelectItem value="230-3-60">230V, 3-phase, 60Hz</SelectItem>
                    <SelectItem value="460-3-60">460V, 3-phase, 60Hz</SelectItem>
                    <SelectItem value="575-3-60">575V, 3-phase, 60Hz</SelectItem>
                    <SelectItem value="other">Other (specify below)</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {electricalSetup === "other" && (
            <FormField
              control={control}
              name="electricalSetupCustom"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom Electrical Setup</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Specify voltage, phase, frequency (e.g., 115V, 1-phase, 60Hz)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name="vfdInstalled"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Variable Frequency Drive (VFD) Installed</FormLabel>
                <FormDescription>Is a VFD currently installed on this pump?</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="vfd-yes" />
                      <Label htmlFor="vfd-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="vfd-no" />
                      <Label htmlFor="vfd-no">No</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {watch("vfdInstalled") === "Yes" && (
            <>
              <FormField
                control={control}
                name="vfdManufacturer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>VFD Manufacturer</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., ABB, Danfoss, Siemens"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="vfdModel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>VFD Model</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter VFD model number"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </div>

        {/* Power Quality Section */}
        <div className="space-y-4">
          <h3 className="text-base font-medium">Power Quality Information</h3>
          <FormField
            control={control}
            name="powerQualityIssues"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Power Quality Issues</FormLabel>
                <FormDescription>Have you experienced any power quality issues with this pump (voltage sags, harmonics, etc.)?</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="power-quality-yes" />
                      <Label htmlFor="power-quality-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="power-quality-no" />
                      <Label htmlFor="power-quality-no">No</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Unknown" id="power-quality-unknown" />
                      <Label htmlFor="power-quality-unknown">Unknown</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {watch("powerQualityIssues") === "Yes" && (
            <FormField
              control={control}
              name="powerQualityDetails"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Power Quality Issue Details</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Describe the power quality issues you've experienced (voltage sags, harmonics, etc.)"
                      className="min-h-[100px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name="harmonicConcerns"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.harmonicConcerns")}</FormLabel>
                <FormDescription>{t("form.harmonicConcernsPrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="harmonic-yes" />
                      <Label htmlFor="harmonic-yes">{t("form.harmonicConcernsYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="harmonic-no" />
                      <Label htmlFor="harmonic-no">{t("form.harmonicConcernsNo")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Unknown" id="harmonic-unknown" />
                      <Label htmlFor="harmonic-unknown">{t("form.harmonicConcernsUnknown")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Additional Electrical Information */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <FormField
          control={control}
          name="electricalNotes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.electricalNotes")}</FormLabel>
              <FormDescription>{t("form.electricalNotesPrompt")}</FormDescription>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={t("form.electricalNotesPlaceholder")}
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// Step 6: Utility Information
export function Step6UtilityInfo() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const hasRateSchedule = watch("hasRateSchedule") === "Yes";
  const hasDemandCharges = watch("hasDemandCharges") === "Yes";
  const participatesInDR = watch("participatesInDemandResponse") === "Yes";

  return (
    <div className="space-y-6">
      {/* Basic Utility Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <FormField
            control={control}
            name="utilityProvider"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.utilityProvider")} *</FormLabel>
                <FormDescription>{t("form.utilityProviderPrompt")}</FormDescription>
                <FormControl>
                  <Input {...field} placeholder={t("form.utilityProviderPlaceholder")} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="utilityRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.utilityRate")} *</FormLabel>
                <FormDescription>{t("form.utilityRatePrompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder={t("form.utilityRatePlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="hasRateSchedule"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.hasRateSchedule")}</FormLabel>
                <FormDescription>{t("form.hasRateSchedulePrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="rate-schedule-yes" />
                      <Label htmlFor="rate-schedule-yes">{t("form.hasRateScheduleYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="rate-schedule-no" />
                      <Label htmlFor="rate-schedule-no">{t("form.hasRateScheduleNo")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Unknown" id="rate-schedule-unknown" />
                      <Label htmlFor="rate-schedule-unknown">{t("form.hasRateScheduleUnknown")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="space-y-4">
          <FormField
            control={control}
            name="utilityAccountNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.utilityAccountNumber")}</FormLabel>
                <FormDescription>{t("form.utilityAccountNumberPrompt")}</FormDescription>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder={t("form.utilityAccountNumberPlaceholder")} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="annualConsumption"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.annualConsumption")}</FormLabel>
                <FormDescription>{t("form.annualConsumptionPrompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    placeholder={t("form.annualConsumptionPlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="hasDemandCharges"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.hasDemandCharges")}</FormLabel>
                <FormDescription>{t("form.hasDemandChargesPrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="demand-charges-yes" />
                      <Label htmlFor="demand-charges-yes">{t("form.hasDemandChargesYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="demand-charges-no" />
                      <Label htmlFor="demand-charges-no">{t("form.hasDemandChargesNo")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Unknown" id="demand-charges-unknown" />
                      <Label htmlFor="demand-charges-unknown">{t("form.hasDemandChargesUnknown")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      
      {/* Rate Schedule Details (conditional) */}
      {hasRateSchedule && (
        <div className="p-4 border border-border rounded-md space-y-4">
          <h3 className="text-base font-medium">{t("form.rateScheduleDetails")}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="rateScheduleName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.rateScheduleName")}</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder={t("form.rateScheduleNamePlaceholder")} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name="rateStructure"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.rateStructure")}</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("form.selectRateStructure")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="flat">{t("form.rateStructureOptions.flat")}</SelectItem>
                      <SelectItem value="tiered">{t("form.rateStructureOptions.tiered")}</SelectItem>
                      <SelectItem value="tou">{t("form.rateStructureOptions.tou")}</SelectItem>
                      <SelectItem value="other">{t("form.rateStructureOptions.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
      
      {/* Demand Charges Details (conditional) */}
      {hasDemandCharges && (
        <div className="p-4 border border-border rounded-md space-y-4">
          <h3 className="text-base font-medium">{t("form.demandChargesDetails")}</h3>
          
          <FormField
            control={control}
            name="demandChargeRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.demandChargeRate")}</FormLabel>
                <FormDescription>{t("form.demandChargeRatePrompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    placeholder={t("form.demandChargeRatePlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      
      {/* Demand Response */}
      <div className="space-y-4 pt-4">
        <FormField
          control={control}
          name="participatesInDemandResponse"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>{t("form.participatesInDemandResponse")}</FormLabel>
              <FormDescription>{t("form.participatesInDemandResponsePrompt")}</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="dr-yes" />
                    <Label htmlFor="dr-yes">{t("form.participatesInDemandResponseYes")}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="dr-no" />
                    <Label htmlFor="dr-no">{t("form.participatesInDemandResponseNo")}</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Interested" id="dr-interested" />
                    <Label htmlFor="dr-interested">{t("form.participatesInDemandResponseInterested")}</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {participatesInDR && (
          <FormField
            control={control}
            name="demandResponseDetails"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.demandResponseDetails")}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t("form.demandResponseDetailsPlaceholder")}
                    className="min-h-[80px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      
      {/* Additional Notes */}
      <div className="space-y-4 pt-4">
        <FormField
          control={control}
          name="utilityNotes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.utilityNotes")}</FormLabel>
              <FormDescription>{t("form.utilityNotesPrompt")}</FormDescription>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={t("form.utilityNotesPlaceholder")}
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// Step 7: Facility Details
export function Step7FacilityDetails() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const facilityType = watch("facilityType");
  const installLocation = watch("installLocation");
  const hasSpaceConstraints = watch("hasSpaceConstraints") === "Yes";

  return (
    <div className="space-y-6">
      {/* Basic Facility Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <FormField
            control={control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.location")} *</FormLabel>
                <FormDescription>{t("form.locationPrompt")}</FormDescription>
                <FormControl>
                  <Input {...field} placeholder={t("form.locationPlaceholder")} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="facilityType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.facilityType")} *</FormLabel>
                <FormDescription>{t("form.facilityTypePrompt")}</FormDescription>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("form.selectFacilityType")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="commercial">{t("form.facilityOptions.commercial")}</SelectItem>
                    <SelectItem value="industrial">{t("form.facilityOptions.industrial")}</SelectItem>
                    <SelectItem value="healthcare">{t("form.facilityOptions.healthcare")}</SelectItem>
                    <SelectItem value="education">{t("form.facilityOptions.education")}</SelectItem>
                    <SelectItem value="data-center">{t("form.facilityOptions.dataCenter")}</SelectItem>
                    <SelectItem value="municipal">{t("form.facilityOptions.municipal")}</SelectItem>
                    <SelectItem value="other">{t("form.facilityOptions.other")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {facilityType === "other" && (
            <FormField
              control={control}
              name="facilityTypeOther"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.facilityTypeOther")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t("form.facilityTypeOtherPlaceholder")} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          
          <FormField
            control={control}
            name="operatingHours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.operatingHours")}</FormLabel>
                <FormDescription>{t("form.operatingHoursPrompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={8760}
                    placeholder={t("form.operatingHoursPlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <FormField
            control={control}
            name="facilityAge"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.facilityAge")}</FormLabel>
                <FormDescription>{t("form.facilityAgePrompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    placeholder={t("form.facilityAgePlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="installLocation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.installLocation")}</FormLabel>
                <FormDescription>{t("form.installLocationPrompt")}</FormDescription>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("form.selectInstallLocation")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="mechanical-room">{t("form.installLocationOptions.mechanicalRoom")}</SelectItem>
                    <SelectItem value="basement">{t("form.installLocationOptions.basement")}</SelectItem>
                    <SelectItem value="roof">{t("form.installLocationOptions.roof")}</SelectItem>
                    <SelectItem value="outdoor">{t("form.installLocationOptions.outdoor")}</SelectItem>
                    <SelectItem value="pump-house">{t("form.installLocationOptions.pumpHouse")}</SelectItem>
                    <SelectItem value="other">{t("form.installLocationOptions.other")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {installLocation === "other" && (
            <FormField
              control={control}
              name="installLocationOther"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.installLocationOther")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t("form.installLocationOtherPlaceholder")} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          
          <FormField
            control={control}
            name="hasSpaceConstraints"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.hasSpaceConstraints")}</FormLabel>
                <FormDescription>{t("form.hasSpaceConstraintsPrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="space-constraints-yes" />
                      <Label htmlFor="space-constraints-yes">{t("form.hasSpaceConstraintsYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="space-constraints-no" />
                      <Label htmlFor="space-constraints-no">{t("form.hasSpaceConstraintsNo")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      
      {/* Space Constraints Details (conditional) */}
      {hasSpaceConstraints && (
        <div className="p-4 border border-border rounded-md space-y-4">
          <h3 className="text-base font-medium">{t("form.spaceConstraintsDetails")}</h3>
          
          <FormField
            control={control}
            name="spaceConstraintsDetails"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.spaceConstraintDescription")}</FormLabel>
                <FormDescription>{t("form.spaceConstraintDescriptionPrompt")}</FormDescription>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t("form.spaceConstraintDescriptionPlaceholder")}
                    className="min-h-[100px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="availableWidth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.availableWidth")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      placeholder={t("form.availableWidthPlaceholder")}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name="availableHeight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.availableHeight")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min={0}
                      placeholder={t("form.availableHeightPlaceholder")}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
      
      {/* Facility Access & Conditions */}
      <div className="space-y-4 pt-2">
        <h3 className="text-base font-medium">{t("form.facilityAccessConditions")}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={control}
            name="accessRestrictions"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.accessRestrictions")}</FormLabel>
                <FormDescription>{t("form.accessRestrictionsPrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="access-restrictions-yes" />
                      <Label htmlFor="access-restrictions-yes">{t("form.accessRestrictionsYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="access-restrictions-no" />
                      <Label htmlFor="access-restrictions-no">{t("form.accessRestrictionsNo")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="environmentalConsiderations"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.environmentalConsiderations")}</FormLabel>
                <FormDescription>{t("form.environmentalConsiderationsPrompt")}</FormDescription>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t("form.environmentalConsiderationsPlaceholder")}
                    className="min-h-[80px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      
      {/* Additional Facility Notes */}
      <div className="space-y-4 pt-2">
        <FormField
          control={control}
          name="facilityNotes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.facilityNotes")}</FormLabel>
              <FormDescription>{t("form.facilityNotesPrompt")}</FormDescription>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={t("form.facilityNotesPlaceholder")}
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// Step 8: Control & Automation
export function Step8Controls() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const controlType = watch("controlType");
  const addControls = watch("addControls") === "Yes";
  const hasAutomation = watch("hasAutomation") === "Yes";
  const hasBms = watch("hasBms") === "Yes";

  return (
    <div className="space-y-6">
      {/* Current Control Strategy */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <FormField
            control={control}
            name="controlType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.controlType")} *</FormLabel>
                <FormDescription>{t("form.controlType_prompt")}</FormDescription>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("form.controlTypePlaceholder")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="constant-speed">{t("form.controlTypes.constantSpeed")}</SelectItem>
                    <SelectItem value="vfd-manual">{t("form.controlTypes.vfdManual")}</SelectItem>
                    <SelectItem value="vfd-bms">{t("form.controlTypes.vfdBms")}</SelectItem>
                    <SelectItem value="vfd-pressure">{t("form.controlTypes.vfdPressure")}</SelectItem>
                    <SelectItem value="vfd-flow">{t("form.controlTypes.vfdFlow")}</SelectItem>
                    <SelectItem value="vfd-temp">{t("form.controlTypes.vfdTemp")}</SelectItem>
                    <SelectItem value="other">{t("form.controlTypes.other")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {controlType === "other" && (
            <FormField
              control={control}
              name="controlTypeOther"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.controlTypeOther")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t("form.controlTypeOtherPlaceholder")} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          
          <FormField
            control={control}
            name="controlDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.controlDescription")}</FormLabel>
                <FormDescription>{t("form.controlDescriptionPrompt")}</FormDescription>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t("form.controlDescriptionPlaceholder")}
                    className="min-h-[80px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="space-y-4">
          <FormField
            control={control}
            name="hasAutomation"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.hasAutomation")}</FormLabel>
                <FormDescription>{t("form.hasAutomationPrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="automation-yes" />
                      <Label htmlFor="automation-yes">{t("form.hasAutomationYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="automation-no" />
                      <Label htmlFor="automation-no">{t("form.hasAutomationNo")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Unknown" id="automation-unknown" />
                      <Label htmlFor="automation-unknown">{t("form.hasAutomationUnknown")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="hasBms"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.hasBms")}</FormLabel>
                <FormDescription>{t("form.hasBmsPrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="bms-yes" />
                      <Label htmlFor="bms-yes">{t("form.hasBmsYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="bms-no" />
                      <Label htmlFor="bms-no">{t("form.hasBmsNo")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Unknown" id="bms-unknown" />
                      <Label htmlFor="bms-unknown">{t("form.hasBmsUnknown")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="controlAge"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Control System Age</FormLabel>
                <FormDescription>How old is the current control system?</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    placeholder="Age in years"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      
      {/* BMS Details (conditional) */}
      {hasBms && (
        <div className="p-4 border border-border rounded-md space-y-4">
          <h3 className="text-base font-medium">BMS System Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="bmsManufacturer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>BMS Manufacturer</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., Johnson Controls, Siemens" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name="bmsModel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>BMS Model</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Model number or system version" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <FormField
            control={control}
            name="bmsIntegrationNotes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>BMS Integration Notes</FormLabel>
                <FormDescription>Provide details about potential integration with this BMS system</FormDescription>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Describe communication protocols, capabilities, limitations, etc."
                    className="min-h-[80px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      
      {/* Control Enhancement Options */}
      <div className="space-y-4 pt-2">
        <FormField
          control={control}
          name="addControls"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Add/Enhance Controls *</FormLabel>
              <FormDescription>Are you interested in adding or enhancing pump controls?</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="add-controls-yes" />
                    <Label htmlFor="add-controls-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="add-controls-no" />
                    <Label htmlFor="add-controls-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Maybe" id="add-controls-maybe" />
                    <Label htmlFor="add-controls-maybe">Maybe</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {addControls && (
          <div className="space-y-4">
            <FormField
              control={control}
              name="desiredControlFeatures"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Desired Control Features</FormLabel>
                  <FormDescription>What control features would you like to add or enhance?</FormDescription>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="e.g., pressure control, flow optimization, remote monitoring"
                      className="min-h-[100px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={control}
              name="integrateWithExisting"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Integrate with Existing Systems</FormLabel>
                  <FormDescription>Should new controls integrate with existing building systems?</FormDescription>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex space-x-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Yes" id="integrate-yes" />
                        <Label htmlFor="integrate-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="No" id="integrate-no" />
                        <Label htmlFor="integrate-no">No</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Unknown" id="integrate-unknown" />
                        <Label htmlFor="integrate-unknown">Unknown</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
      
      {/* Additional Control Notes */}
      <div className="space-y-4 pt-2">
        <FormField
          control={control}
          name="controlNotes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Additional Control Notes</FormLabel>
              <FormDescription>Any other control-related information that might be relevant</FormDescription>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Other control requirements, challenges, considerations, etc."
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// Step 9: Retrofit Constraints
export function Step9RetrofitConstraints() {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const hasBudgetConstraints = watch("hasBudgetConstraints") === "Yes";
  const hasConstructionConstraints = watch("hasConstructionConstraints") === "Yes";
  const hasPermittingConstraints = watch("hasPermittingConstraints") === "Yes";
  const hasSchedulingConstraints = watch("hasSchedulingConstraints") === "Yes";

  return (
    <div className="space-y-6">
      {/* General Constraints */}
      <div className="space-y-4">
        <FormField
          control={control}
          name="retrofitConstraints"
          render={({ field }) => (
            <FormItem>
              <FormLabel>General Retrofit Constraints</FormLabel>
              <FormDescription>Describe any general constraints for the retrofit project</FormDescription>
              <FormControl>
                <Textarea 
                  {...field}
                  className="min-h-[100px]"
                  placeholder="Facility operational requirements, accessibility issues, etc."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      {/* Budget Constraints */}
      <div className="space-y-4">
        <FormField
          control={control}
          name="hasBudgetConstraints"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Budget Constraints</FormLabel>
              <FormDescription>Are there specific budget constraints for this project?</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="budget-constraints-yes" />
                    <Label htmlFor="budget-constraints-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="budget-constraints-no" />
                    <Label htmlFor="budget-constraints-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Unknown" id="budget-constraints-unknown" />
                    <Label htmlFor="budget-constraints-unknown">Unknown</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {hasBudgetConstraints && (
          <div className="p-4 border border-border rounded-md space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={control}
                name="budgetAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Budget Amount</FormLabel>
                    <FormDescription>Approximate budget amount in dollars</FormDescription>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={0}
                        placeholder="e.g., 25000"
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={control}
                name="budgetTimeframe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Budget Timeframe</FormLabel>
                    <FormDescription>When is the budget available?</FormDescription>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select budget timeframe" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="current-year">Current fiscal year</SelectItem>
                        <SelectItem value="next-year">Next fiscal year</SelectItem>
                        <SelectItem value="multi-year">Multiple years</SelectItem>
                        <SelectItem value="unallocated">Not yet allocated</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={control}
              name="budgetNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Budget Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Any additional budget information or constraints"
                      className="min-h-[80px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
      
      {/* Construction & Installation Constraints */}
      <div className="space-y-4">
        <FormField
          control={control}
          name="hasConstructionConstraints"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Construction & Installation Constraints</FormLabel>
              <FormDescription>Are there construction or installation limitations?</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="construction-constraints-yes" />
                    <Label htmlFor="construction-constraints-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="construction-constraints-no" />
                    <Label htmlFor="construction-constraints-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Unknown" id="construction-constraints-unknown" />
                    <Label htmlFor="construction-constraints-unknown">Unknown</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {hasConstructionConstraints && (
          <FormField
            control={control}
            name="constructionConstraintsDetails"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Construction & Installation Details</FormLabel>
                <FormDescription>Describe the specific construction or installation constraints</FormDescription>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Limited space, difficult access, scheduling restrictions, etc."
                    className="min-h-[100px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      
      {/* Permitting & Compliance */}
      <div className="space-y-4">
        <FormField
          control={control}
          name="hasPermittingConstraints"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Permitting & Compliance Concerns</FormLabel>
              <FormDescription>Are there permitting or regulatory compliance issues to consider?</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="permitting-constraints-yes" />
                    <Label htmlFor="permitting-constraints-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="permitting-constraints-no" />
                    <Label htmlFor="permitting-constraints-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Unknown" id="permitting-constraints-unknown" />
                    <Label htmlFor="permitting-constraints-unknown">Unknown</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {hasPermittingConstraints && (
          <FormField
            control={control}
            name="permittingConstraintsDetails"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Permitting & Compliance Details</FormLabel>
                <FormDescription>Describe specific permitting or regulatory issues</FormDescription>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Required permits, regulatory compliance requirements, etc."
                    className="min-h-[100px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      
      {/* Scheduling Constraints */}
      <div className="space-y-4">
        <FormField
          control={control}
          name="hasSchedulingConstraints"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Scheduling Constraints</FormLabel>
              <FormDescription>Are there timing or scheduling constraints for implementation?</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="scheduling-constraints-yes" />
                    <Label htmlFor="scheduling-constraints-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="scheduling-constraints-no" />
                    <Label htmlFor="scheduling-constraints-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Unknown" id="scheduling-constraints-unknown" />
                    <Label htmlFor="scheduling-constraints-unknown">Unknown</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {hasSchedulingConstraints && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={control}
                name="earliestStartDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Earliest Start Date</FormLabel>
                    <FormDescription>When can work begin at the earliest?</FormDescription>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? format(field.value, "PPP") : <span>{t("form.selectDate")}</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={control}
                name="requiredCompletionDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Required Completion Date</FormLabel>
                    <FormDescription>When must work be completed by?</FormDescription>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? format(field.value, "PPP") : <span>{t("form.selectDate")}</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={control}
              name="schedulingNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scheduling Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Additional scheduling details or constraints"
                      className="min-h-[80px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
      
      {/* Additional Constraints */}
      <div className="space-y-4">
        <FormField
          control={control}
          name="additionalConstraints"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Additional Constraints</FormLabel>
              <FormDescription>Any other constraints or limitations not covered above</FormDescription>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Other project constraints, stakeholder requirements, etc."
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// Step 10: Supporting Documents
export function Step10Documents({ onFileChange }: { onFileChange: (file: File | null) => void }) {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const [hasUploaded, setHasUploaded] = useState(false);

  return (
    <div className="space-y-6">
      {/* Primary Documents Section */}
      <div className="space-y-4">
        <h3 className="text-base font-medium">Required Documents</h3>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="datasheet">Pump/Motor Datasheet *</Label>
            <p className="text-sm text-muted-foreground mb-2">Upload the pump/motor manufacturer's datasheet</p>
            <Input
              id="datasheet"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                if (file && file.size > 10 * 1024 * 1024) {
                  // File is too large
                  alert("File is too large! Maximum size is 10MB.");
                  e.target.value = "";
                  onFileChange(null);
                  setHasUploaded(false);
                } else {
                  onFileChange(file);
                  if (file) setHasUploaded(true);
                }
              }}
            />
            <p className="mt-1 text-xs text-muted-foreground">Accepted file formats: PDF, JPG, PNG. Maximum size: 10MB</p>
          </div>
          
          {hasUploaded && (
            <Alert className="bg-green-50 border-green-200">
              <AlertCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-600">
                File uploaded successfully!
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
      
      {/* Additional Documentation */}
      <div className="space-y-4">
        <h3 className="text-base font-medium">Additional Documentation</h3>
        
        <FormField
          control={control}
          name="hasOperatingManual"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Operating Manual Available</FormLabel>
              <FormDescription>Is an operating manual available for the existing equipment?</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="has-manual-yes" />
                    <Label htmlFor="has-manual-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="has-manual-no" />
                    <Label htmlFor="has-manual-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Unknown" id="has-manual-unknown" />
                    <Label htmlFor="has-manual-unknown">Unknown</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={control}
          name="hasMaintenanceRecords"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Maintenance Records Available</FormLabel>
              <FormDescription>Are maintenance records available for the existing equipment?</FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="has-records-yes" />
                    <Label htmlFor="has-records-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="has-records-no" />
                    <Label htmlFor="has-records-no">No</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Unknown" id="has-records-unknown" />
                    <Label htmlFor="has-records-unknown">Unknown</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      {/* Document Notes */}
      <div className="space-y-4">
        <FormField
          control={control}
          name="documentationNotes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Documentation Notes</FormLabel>
              <FormDescription>Any additional notes about available documentation</FormDescription>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Additional documentation details, availability, format, etc."
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// Step 11: Analytics Parameters
export function Step11Analytics() {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();
  const sensitivityVariables = watch("sensitivityVariables") as SensitivityVariable[];
  const includeMonteCarloSimulation = watch("includeMonteCarloSimulation") === "Yes";
  const includeCarbonAnalysis = watch("includeCarbonAnalysis") === "Yes";

  return (
    <div className="space-y-6">
      {/* Basic Financial Parameters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-base font-medium">{t("form.financialParameters")}</h3>
          
          <FormField
            control={control}
            name="lifecycleYears"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.lifecycleYears")}</FormLabel>
                <FormDescription>{t("form.lifecycleYears_prompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={1}
                    max={30}
                    placeholder={t("form.lifecycleYearsPlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="discountRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.discountRate")}</FormLabel>
                <FormDescription>{t("form.discountRate_prompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={20}
                    step={0.1}
                    placeholder={t("form.discountRatePlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="inflationRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.inflationRate")}</FormLabel>
                <FormDescription>{t("form.inflationRate_prompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={10}
                    step={0.1}
                    placeholder={t("form.inflationRatePlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="space-y-4">
          <h3 className="text-base font-medium">{t("form.costParameters")}</h3>
          
          <FormField
            control={control}
            name="includeMaterialCosts"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.includeMaterialCosts")}</FormLabel>
                <FormDescription>{t("form.includeMaterialCostsPrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="include-material-yes" />
                      <Label htmlFor="include-material-yes">{t("form.includeMaterialCostsYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="include-material-no" />
                      <Label htmlFor="include-material-no">{t("form.includeMaterialCostsNo")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="electricityRateEscalation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.electricityRateEscalation")}</FormLabel>
                <FormDescription>{t("form.electricityRateEscalationPrompt")}</FormDescription>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    min={0}
                    max={10}
                    step={0.1}
                    placeholder={t("form.electricityRateEscalationPlaceholder")}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      
      {/* Advanced Analytics Options */}
      <div className="space-y-4">
        <h3 className="text-base font-medium">{t("form.advancedAnalytics")}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={control}
            name="includeMonteCarloSimulation"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.includeMonteCarloSimulation")}</FormLabel>
                <FormDescription>{t("form.includeMonteCarloSimulationPrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="monte-carlo-yes" />
                      <Label htmlFor="monte-carlo-yes">{t("form.includeMonteCarloSimulationYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="monte-carlo-no" />
                      <Label htmlFor="monte-carlo-no">{t("form.includeMonteCarloSimulationNo")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="includeCarbonAnalysis"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>{t("form.includeCarbonAnalysis")}</FormLabel>
                <FormDescription>{t("form.includeCarbonAnalysisPrompt")}</FormDescription>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="carbon-analysis-yes" />
                      <Label htmlFor="carbon-analysis-yes">{t("form.includeCarbonAnalysisYes")}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="carbon-analysis-no" />
                      <Label htmlFor="carbon-analysis-no">{t("form.includeCarbonAnalysisNo")}</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      
      {/* Sensitivity Analysis */}
      <div className="space-y-4">
        <h3 className="text-base font-medium">{t("form.sensitivityAnalysis")}</h3>
        <p className="text-sm text-muted-foreground mb-2">{t("form.sensitivityAnalysisPrompt")}</p>
        <div className="mt-2 space-y-2">
          {sensitivityVariables?.map((variable, index) => (
            <div key={variable.name} className="flex items-center">
              <Checkbox
                id={`sensitivity-${variable.name}`}
                checked={variable.selected}
                onCheckedChange={(checked) => {
                  const newVariables = [...sensitivityVariables];
                  newVariables[index] = {
                    ...newVariables[index],
                    selected: !!checked
                  };
                  setValue("sensitivityVariables", newVariables);
                }}
              />
              <Label
                htmlFor={`sensitivity-${variable.name}`}
                className="ml-2 text-sm"
              >
                {t(`form.sensitivityVariables.${variable.name}`)}
              </Label>
            </div>
          ))}
        </div>
      </div>
      
      {/* Analysis Notes */}
      <div className="space-y-4">
        <FormField
          control={control}
          name="analysisNotes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("form.analysisNotes")}</FormLabel>
              <FormDescription>{t("form.analysisNotesPrompt")}</FormDescription>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder={t("form.analysisNotesPlaceholder")}
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
